// src/pages/cash_management/CashManagement.jsx
import { useEffect, useMemo, useState } from "react";

import {
  useCashBalance,
  useCashTransactions,
  useClinics,
  useCreateCashTransaction,
  useCreateNetsuiteIntercompanyJournalEntry,
  useCreateNetsuiteJournalEntry,
  useDepositCashToSafe,
  useEligibleCashUsers,
  useSearchNetsuiteCashManagementJournalEntries,
  useSearchNetsuiteAdvancedIntercompanyJournalEntries,
  useSearchNetsuiteCashManagementJe,
  useUpdateCashTransaction,
  useNetsuiteAccounts,
} from "@api/hooks";
import { useAuth } from "@api/useAuth";
import {
  Button,
  Card,
  ConfirmModal,
  DataStateHandler,
  Loading,
  MonthlyClinicSelection,
  Page,
  Pagination,
  Tooltip,
  getCurrentMonth,
  showApiError,
  showError,
  showSuccess,
} from "@common";
import {
  canAccessAllClinics,
  canWriteClinicData,
  clinicLabel,
  formatMoney,
  getMonthRange,
  isObserver,
  isOrganizerOrAdmin,
} from "@utils";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import { NETSUITE_ACCOUNT_IDS } from "@utils/contants";
import DepositToSafePreview from "./DepositToSafePreview";
import ReverseJournalEntryPreview from "./ReverseJournalEntryPreview";
import ClinicCashCard from "./ClinicCashCard";
import { todayIso } from "./format";

/**
 * Resolve the as_of date for a given month (YYYY-MM).
 * - For the current month, use today (so balance reflects "right now").
 * - For past months, use the last day of that month.
 */
function asOfFromMonth(month) {
  if (!month) return todayIso();
  const current = getCurrentMonth();
  if (month === current) return todayIso();
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return todayIso();
  const last = new Date(Date.UTC(y, m, 0));
  const yy = last.getUTCFullYear();
  const mm = String(last.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(last.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function rowsOf(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.results || [];
}

// ── Reverse-entry memos ──────────────────────────────────────────────────────
// One memo per record type, because the entry means something different in each
// case and the listing finds each record type by its memo:
//   - MULTI-subsidiary → advanced intercompany JE. Memo is a starting point and
//     stays editable.
//   - SINGLE-subsidiary → normal JE (NetSuite rejects an advanced intercompany
//     one with no second subsidiary). Those are always the same movement — SFC
//     cash into an SFC bank account — so the memo is fixed and LOCKED rather
//     than left to be typed differently every time.
// Changing either string breaks the corresponding listing query below; they are
// the join key between what we write and what we can find again.
const REVERSE_JE_MEMO = "Reverse Journal Entry - Smiles Analytics";
const CASH_TO_BANK_JE_MEMO = "Cash to Bank of SFC - Smiles Analytics";

const memoWithDate = (base, trandate) => (trandate ? `${base} - ${trandate}` : base);

// Distinct `lineSubsidiary` ids across the built lines. Lines with no subsidiary
// (nothing selected yet) don't count.
function distinctLineSubsidiaries(items = []) {
  return [
    ...new Set(items.map((it) => String(it.lineSubsidiary?.id ?? "")).filter((id) => id !== "")),
  ];
}

// An advanced intercompany JE needs at least TWO subsidiaries. One (or none, before
// a bank account is picked) means the entry has to be posted as a normal JE.
function isSingleSubsidiaryEntry(items) {
  return distinctLineSubsidiaries(items).length <= 1;
}

/**
 * Multi-clinic Cash Management.
 *
 * Two usage modes:
 *
 * 1. **Controlled** (Admin / Management tabs) — parent renders the
 *    <MonthlyClinicSelection /> in its stat-card row and passes
 *    `month` + `clinics` down. CashManagement renders only the body.
 *
 * 2. **Standalone** (`/cash-management` route) — when neither prop is provided,
 *    CashManagement loads the user's accessible clinics, renders its own
 *    MonthlyClinicSelection at the top, and wraps everything in <Page>.
 *
 * Issues exactly ONE call per data type regardless of how many clinics
 * are selected:
 *   - balance       → /balance/?clinic=A,B,C&as_of=YYYY-MM-DD
 *   - eligible      → /eligible-users/?clinic=A,B,C
 *   - deposit txns  → /?clinic=A,B,C&status=confirmed&kind=deposit
 *   - deposit JEs   → NetSuite search over the subsidiaries + dates those imply
 *
 * The pending-inbox and history listings are the exception: ClinicCashCard
 * fetches them per clinic with limit/offset, because a page of one clinic's
 * table cannot be sliced out of a response merged across every clinic.
 *
 * Props (all optional):
 *   - month:   'YYYY-MM'        selected month (controls as_of)
 *   - clinics: ['A','B',...]    selected clinic codes
 */
export default function CashManagement({ month, date: controlledDate, clinics }) {
  const isControlled = month !== undefined || controlledDate !== undefined || clinics !== undefined;
  const noRefreshOptions = {
    refetchInterval: false,
    refetchIntervalInBackground: false,
  };

  const { user } = useAuth();
  const userId = user?.id;

  // Fetch account CASH_SFC only if user has the right role
  const canViewAccount = canAccessAllClinics(user);
  const { data: accountData } = useNetsuiteAccounts(
    canViewAccount ? { id: NETSUITE_ACCOUNT_IDS.CASH_SFC } : {},
    { enabled: canViewAccount, ...noRefreshOptions },
  );
  const account =
    canViewAccount && accountData?.items && accountData.items.length > 0
      ? accountData.items[0]
      : null;

  // Admin/organizer also need clinic NetSuite IDs to build the deposit JE.
  const isAdminOrOrg = isOrganizerOrAdmin(user);
  // Observer sees everything admin sees (all transactions, NetSuite JEs) but
  // keeps `isAdminOrOrg` false, so admin-only actions stay hidden.
  const canSeeAdminData = isAdminOrOrg || isObserver(user);
  const { data: adminClinicsData } = useClinics(
    {},
    { enabled: canSeeAdminData && !!user?.id, ...noRefreshOptions },
  );
  const clinicByCode = useMemo(() => {
    const list = adminClinicsData?.results || adminClinicsData || [];
    const map = {};
    for (const c of list) {
      if (c?.clinic) map[String(c.clinic).toUpperCase()] = c;
    }
    return map;
  }, [adminClinicsData]);

  // -------- Standalone state (only used when not controlled) -----------------
  const canReadAll = canAccessAllClinics(user);
  // Only fetch the clinic catalogue for roles that have list permission
  // (Organizer, Admin, Observer, Payroll). Clinic-scoped users (AM, PL,
  // Bookkeeper) already know their codes from user.clinic.
  const { data: clinicsData, isLoading: clinicsLoading } = useClinics(
    {},
    { enabled: !isControlled && canReadAll && !!userId, ...noRefreshOptions },
  );
  const clinicOptions = useMemo(() => {
    if (isControlled) return [];
    if (canReadAll) {
      const list = clinicsData?.results || clinicsData || [];
      return list
        .filter((c) => c?.clinic)
        .map((c) => ({ value: c.clinic, label: clinicLabel(c) }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    return (user?.clinic || []).map((code) => ({ value: code, label: code }));
  }, [isControlled, canReadAll, clinicsData, user?.clinic]);

  const [ownMonths, setOwnMonths] = useState(() => [getCurrentMonth()]);
  const [ownSelectedClinics, setOwnSelectedClinics] = useState([]);

  // -------- Resolve effective month + clinic list ----------------------------
  const effectiveMonth = isControlled ? month || "" : ownMonths[0] || "";
  const effectiveClinics = useMemo(() => {
    if (isControlled) return clinics || [];
    // Empty means NOTHING selected, not every clinic — same rule as the
    // clinic filter bar elsewhere. Every query below is gated on `hasClinics`,
    // so nothing is fetched until a clinic is picked.
    return ownSelectedClinics;
  }, [isControlled, clinics, ownSelectedClinics]);

  // -------- Optional day filter (within the selected month) -----------------
  const monthRange = useMemo(
    () => getMonthRange(effectiveMonth) || { from: "", to: "" },
    [effectiveMonth],
  );
  const [selectedDay, setSelectedDay] = useState("");
  // Reset the day filter whenever it falls outside the current month range.
  useEffect(() => {
    if (controlledDate) return;
    if (!selectedDay) return;
    if (!monthRange.from || selectedDay < monthRange.from || selectedDay > monthRange.to) {
      setSelectedDay("");
    }
  }, [controlledDate, monthRange, selectedDay]);

  // A controlled `date` prop overrides the inline day filter.
  const effectiveDay = controlledDate || selectedDay;
  const date = useMemo(
    () => effectiveDay || asOfFromMonth(effectiveMonth),
    [effectiveDay, effectiveMonth],
  );
  const uniqueClinics = useMemo(
    () =>
      Array.from(new Set((effectiveClinics || []).filter(Boolean))).map((c) =>
        String(c).toUpperCase(),
      ),
    [effectiveClinics],
  );
  const clinicCsv = uniqueClinics.join(",");
  const hasClinics = uniqueClinics.length > 0;

  // ---- Single fetch each, scoped to all selected clinics --------------------
  const balanceQuery = useCashBalance(hasClinics ? { clinic: clinicCsv, as_of: date } : {}, {
    enabled: hasClinics,
    ...noRefreshOptions,
  });
  const usersQuery = useEligibleCashUsers(
    hasClinics ? { clinic: clinicCsv, exclude_self: "true" } : {},
    { enabled: hasClinics, ...noRefreshOptions },
  );
  // All confirmed Deposit-to-Safe cash transactions for the selected
  // clinics. Used to gather (subsidiary, occurred_on) pairs that we then
  // pass to the NetSuite cash-management JE search.
  const depositTxQuery = useCashTransactions(
    hasClinics
      ? {
          clinic: clinicCsv,
          status: "confirmed",
          kind: "deposit",
        }
      : {},
    { enabled: hasClinics, ...noRefreshOptions },
  );

  // Existing Deposit-to-Safe transaction lines in NetSuite, filtered by
  // the subsidiaries + dates derived from confirmed deposit cash
  // transactions, and the deposit memo prefix.
  const depositMemoSearch = "Cash Tranfer - Smiles Analytics";

  const { depositSubsidiariesForSearch, depositDatesForSearch } = useMemo(() => {
    const subs = new Set();
    const dates = new Set();
    for (const tx of rowsOf(depositTxQuery.data)) {
      const code = (tx.clinic_code || "").toUpperCase();
      const sid = clinicByCode[code]?.netsuite_subsidiary_id;
      if (sid !== null && sid !== undefined && sid !== "") {
        subs.add(String(sid));
      }
      if (tx.occurred_on) dates.add(tx.occurred_on);
    }
    return {
      depositSubsidiariesForSearch: Array.from(subs),
      depositDatesForSearch: Array.from(dates),
    };
  }, [depositTxQuery.data, clinicByCode]);

  const depositJeSearchQuery = useSearchNetsuiteCashManagementJournalEntries(
    {
      memo: depositMemoSearch,
      subsidiaries: depositSubsidiariesForSearch,
      dates: depositDatesForSearch,
    },
    {
      enabled:
        canSeeAdminData &&
        depositSubsidiariesForSearch.length > 0 &&
        depositDatesForSearch.length > 0,
      ...noRefreshOptions,
    },
  );

  // Two queries, one per record type — each searching the memo IT is written with,
  // which is why the memo constants are the join key between create and list:
  //   1. Advanced intercompany JEs           → REVERSE_JE_MEMO
  //   2. Normal JEs (single-subsidiary ones)  → CASH_TO_BANK_JE_MEMO
  // Searching the reverse memo against the normal-JE table would find nothing,
  // since those entries are written with the locked cash-to-bank memo.
  const reverseJeMemoSearch = REVERSE_JE_MEMO;

  const [reverseJePage, setReverseJePage] = useState(1);
  const [reverseJePageSize, setReverseJePageSize] = useState(5);

  // Both endpoints take limit/offset, but a page of the MERGED listing cannot be
  // asked of either one alone: row 6 overall might be row 1 of one table and row
  // 5 of the other. Since both come back sorted newest-first, taking the first
  // (page x pageSize) rows of each is guaranteed to contain the requested page —
  // merge, sort, then slice off the tail. Still bounded: at page 1 with size 5
  // that is 5 rows per endpoint instead of every reverse JE ever posted.
  const reverseJeFetchLimit = reverseJePage * reverseJePageSize;

  const reverseJeListQuery = useSearchNetsuiteAdvancedIntercompanyJournalEntries(
    { memo: reverseJeMemoSearch, limit: reverseJeFetchLimit, offset: 0 },
    { enabled: canSeeAdminData, ...noRefreshOptions },
  );
  // No subsidiary filter: the memo is specific enough, and it keeps the listing
  // honest if an entry ever lands somewhere other than SFC.
  const reversePlainJeListQuery = useSearchNetsuiteCashManagementJe(
    { memo: CASH_TO_BANK_JE_MEMO, limit: reverseJeFetchLimit, offset: 0 },
    { enabled: canSeeAdminData, ...noRefreshOptions },
  );

  // Grand total across both tables — `count` is the unpaged total, not the size
  // of the returned page, so the pager knows how many pages there really are.
  const reverseJeTotal =
    (Number(reverseJeListQuery.data?.count) || 0) +
    (Number(reversePlainJeListQuery.data?.count) || 0);

  const reverseJeList = useMemo(() => {
    const rows = (data) => data?.items ?? data?.results ?? (Array.isArray(data) ? data : []);
    // `record_type` comes from the new endpoint; the advanced search predates it,
    // so its rows are labelled here.
    const merged = [
      ...rows(reverseJeListQuery.data).map((je) => ({
        ...je,
        record_type: je.record_type ?? "advIntercompanyJournalEntry",
      })),
      ...rows(reversePlainJeListQuery.data),
    ];
    // trandate arrives in NetSuite's display format ("8/13/2026"), which cannot be
    // compared as text — "12/1/2025" would sort after it. Parse to a timestamp
    // (ISO tolerated too, in case a caller TO_CHAR's it later), newest first.
    const toTime = (value) => {
      const s = String(value ?? "").trim();
      const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
      if (iso) return Date.UTC(+iso[1], +iso[2] - 1, +iso[3]);
      const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s);
      if (us) return Date.UTC(+us[3], +us[1] - 1, +us[2]);
      return 0;
    };
    return merged.sort((a, b) => toTime(b.trandate) - toTime(a.trandate));
  }, [reverseJeListQuery.data, reversePlainJeListQuery.data]);

  // Keep the page in range when the totals shrink (a refetch returning fewer rows
  // would otherwise leave the table showing an empty page).
  const reverseJeTotalPages = Math.max(1, Math.ceil(reverseJeTotal / reverseJePageSize));
  useEffect(() => {
    if (reverseJePage > reverseJeTotalPages) setReverseJePage(reverseJeTotalPages);
  }, [reverseJePage, reverseJeTotalPages]);

  const reverseJePageRows = useMemo(() => {
    const start = (reverseJePage - 1) * reverseJePageSize;
    return reverseJeList.slice(start, start + reverseJePageSize);
  }, [reverseJeList, reverseJePage, reverseJePageSize]);

  // ---- Group results per clinic ---------------------------------------------
  const balanceByCode = useMemo(() => {
    const map = {};
    for (const b of rowsOf(balanceQuery.data)) {
      if (b.clinic) map[String(b.clinic).toUpperCase()] = b;
    }
    return map;
  }, [balanceQuery.data]);

  // Map subsidiary id -> clinic code, then group existing JEs by clinic.
  const codeBySubsidiaryId = useMemo(() => {
    const map = {};
    for (const code of uniqueClinics) {
      const sid = clinicByCode[code]?.netsuite_subsidiary_id;
      if (sid !== null && sid !== undefined && sid !== "") {
        map[String(sid)] = code;
      }
    }
    return map;
  }, [uniqueClinics, clinicByCode]);

  const depositJesByCode = useMemo(() => {
    const map = {};
    for (const code of uniqueClinics) map[code] = [];
    const data = depositJeSearchQuery.data;
    const items = data?.items ?? data?.results ?? (Array.isArray(data) ? data : []);
    if (!Array.isArray(items)) return map;
    for (const it of items) {
      const sid = it?.subsidiary_id ?? it?.subsidiaryid;
      if (sid === null || sid === undefined) continue;
      const code = codeBySubsidiaryId[String(sid)];
      if (code && map[code]) map[code].push(it);
    }
    return map;
  }, [depositJeSearchQuery.data, uniqueClinics, codeBySubsidiaryId]);

  const receiverOptionsByCode = useMemo(() => {
    const map = {};
    for (const code of uniqueClinics) map[code] = [];
    const meId = userId != null ? String(userId) : "";
    for (const u of usersQuery.data?.results || []) {
      // A giver can never send to themselves — defensively filter the
      // current user out even though the backend should already exclude them.
      if (meId && String(u.id) === meId) continue;
      const opt = {
        label: `${u.full_name || u.email} (${u.role_name || "—"})`,
        value: u.id,
      };
      const codes = (u.clinics || []).map((c) => String(c).toUpperCase());
      // Backwards-compat: if the API didn't return `clinics`, treat the user
      // as eligible everywhere.
      const targetCodes = codes.length ? codes : uniqueClinics;
      for (const code of targetCodes) {
        if (map[code]) map[code].push(opt);
      }
    }
    return map;
  }, [usersQuery.data, uniqueClinics, userId]);

  // ---- Mutations -------------------------------------------------------------
  const createMutation = useCreateCashTransaction();
  const updateMutation = useUpdateCashTransaction();
  const depositMutation = useDepositCashToSafe();
  const createJeMut = useCreateNetsuiteIntercompanyJournalEntry();
  // NetSuite rejects an advanced intercompany JE whose lines all sit in ONE
  // subsidiary — there is no intercompany leg to record. When the reverse entry
  // ends up single-subsidiary (the selected bank account belongs to SFC itself),
  // it is posted as a plain journalEntry through this mutation instead.
  const createPlainJeMut = useCreateNetsuiteJournalEntry();
  // The Reverse JE modal can submit through either mutation, so its busy state is
  // the union of both.
  const reverseJeSending = createJeMut.isPending || createPlainJeMut.isPending;
  const busy =
    createMutation.isPending ||
    updateMutation.isPending ||
    depositMutation.isPending ||
    createJeMut.isPending ||
    createPlainJeMut.isPending;

  // Deposit-to-safe Journal Entry preview state.
  // { clinicCode, amount, payload } | null
  const [depositPreview, setDepositPreview] = useState(null);
  // Area Manager / Practitioner Lead deposit: { clinicCode, amount } | null.
  // No editable JE preview — the backend builds the entry from the clinic's
  // NetSuite accounts and the user's own available cash.
  const [directDeposit, setDirectDeposit] = useState(null);

  // Reverse Journal Entry preview state.
  // { amount, bankAccountId, bankSubsidiaryId, additionalLines, payload } | null
  // additionalLines = [{ accountId, isDebit }, ...]
  const [reverseJePreview, setReverseJePreview] = useState(null);

  // The selected bank account's debit is ALWAYS the Amount from the top of the
  // preview — it is not separately editable. The two sides of this entry describe
  // one movement of one sum, so letting the bank leg differ from the SFC leg could
  // only ever produce a lopsided entry. `additionalLines` are the escape hatch for
  // anything that genuinely needs its own amount; each carries its own loan
  // counterpart, so it balances within its own subsidiary.
  const buildReverseJeLines = (
    amt,
    bankAccountId = "",
    bankSubsidiaryId = "",
    additionalLines = [],
  ) => {
    const sfcId = String(NETSUITE_ACCOUNT_IDS.SMILES_FIRST_CORPORATION);
    const cashSfcId = String(NETSUITE_ACCOUNT_IDS.CASH_SFC);
    const loanId = String(NETSUITE_ACCOUNT_IDS.LOAN_FROM_SMILES_FIRST_CORPORATION);
    const a = Number(amt) || 0;
    const bankSubId = bankSubsidiaryId ? String(bankSubsidiaryId) : sfcId;
    const lines = [
      // SFC subsidiary balances internally: loan debit a, cash credit a.
      { lineSubsidiary: { id: sfcId }, account: { id: cashSfcId }, credit: a },
      { lineSubsidiary: { id: sfcId }, account: { id: loanId }, debit: a },
      // Bank subsidiary balances internally: bank debit, loan credit — both `a`.
      { lineSubsidiary: { id: bankSubId }, account: { id: loanId }, credit: a },
      {
        lineSubsidiary: { id: bankSubId },
        account: { id: bankAccountId ? String(bankAccountId) : "" },
        debit: a,
        __bank: true,
      },
    ];
    // Add additional bank subsidiary lines. Each one needs its own loan
    // counterpart within the same subsidiary so debits and credits balance
    // within every subsidiary.
    for (const extra of additionalLines) {
      const extraAmount = Number(extra.amount) || 0;
      const extraSubId = extra.subsidiaryId ? String(extra.subsidiaryId) : bankSubId;
      // Loan counterpart (credit) within the same bank subsidiary.
      lines.push({
        lineSubsidiary: { id: extraSubId },
        account: { id: loanId },
        credit: extraAmount,
      });
      lines.push({
        lineSubsidiary: { id: extraSubId },
        account: { id: extra.accountId ? String(extra.accountId) : "" },
        debit: extraAmount,
        __extra: true,
      });
    }
    return lines;
  };

  const handleOpenReverseJe = () => {
    const trandate = todayIso();
    const sfcId = String(NETSUITE_ACCOUNT_IDS.SMILES_FIRST_CORPORATION);
    const amount = 0;
    const bankAccountId = "";
    const bankSubsidiaryId = "";
    const additionalLines = [];
    const items = buildReverseJeLines(amount, bankAccountId, bankSubsidiaryId, additionalLines);
    // Opens single-subsidiary: with no bank account chosen yet every line is SFC.
    // Picking a non-SFC account flips it to intercompany and unlocks the memo.
    const isSingle = isSingleSubsidiaryEntry(items);
    const payload = {
      subsidiary: { id: sfcId },
      memo: memoWithDate(isSingle ? CASH_TO_BANK_JE_MEMO : REVERSE_JE_MEMO, trandate),
      trandate,
      line: { items },
    };
    setReverseJePreview({
      amount,
      bankAccountId,
      bankSubsidiaryId,
      additionalLines,
      isSingleSubsidiary: isSingle,
      payload,
    });
  };

  const handleReverseJeChange = (patch) => {
    setReverseJePreview((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      const nextPayload = { ...prev.payload };
      let rebuildLines = false;
      if (Object.prototype.hasOwnProperty.call(patch, "amount")) {
        next.amount = patch.amount;
        rebuildLines = true;
      }
      if (Object.prototype.hasOwnProperty.call(patch, "bankAccountId")) {
        next.bankAccountId = patch.bankAccountId;
        rebuildLines = true;
      }
      if (Object.prototype.hasOwnProperty.call(patch, "bankSubsidiaryId")) {
        next.bankSubsidiaryId = patch.bankSubsidiaryId;
        rebuildLines = true;
      }
      if (Object.prototype.hasOwnProperty.call(patch, "additionalLines")) {
        next.additionalLines = patch.additionalLines;
        rebuildLines = true;
      }
      // trandate/memo are applied BEFORE the memo rule below, so a locked memo is
      // rebuilt against the new date and a manual memo edit can still be rejected.
      if (Object.prototype.hasOwnProperty.call(patch, "trandate")) {
        nextPayload.trandate = patch.trandate;
      }
      if (Object.prototype.hasOwnProperty.call(patch, "memo")) {
        nextPayload.memo = patch.memo;
      }
      if (rebuildLines) {
        nextPayload.line = {
          ...nextPayload.line,
          items: buildReverseJeLines(
            next.amount,
            next.bankAccountId,
            next.bankSubsidiaryId,
            next.additionalLines,
          ),
        };
      }

      // Memo follows the record type the entry will be posted as.
      const isSingle = isSingleSubsidiaryEntry(nextPayload.line?.items ?? []);
      next.isSingleSubsidiary = isSingle;
      if (isSingle) {
        // Locked: always the cash-to-bank wording, always carrying the current
        // date. Any `memo` patch that slipped through is overwritten here, so the
        // stored memo can never disagree with the read-only field shown.
        nextPayload.memo = memoWithDate(CASH_TO_BANK_JE_MEMO, nextPayload.trandate);
      } else if (prev.isSingleSubsidiary) {
        // Just unlocked (a non-SFC bank account was picked). Seed the editable
        // intercompany memo — the locked one no longer describes the entry.
        nextPayload.memo = memoWithDate(REVERSE_JE_MEMO, nextPayload.trandate);
      }

      next.payload = nextPayload;
      return next;
    });
  };

  const confirmReverseJe = async () => {
    if (!reverseJePreview) return;
    const amt = Number(reverseJePreview.amount) || 0;
    if (amt <= 0) {
      showError("Amount must be greater than 0.");
      return;
    }
    if (!reverseJePreview.bankAccountId) {
      showError("Please select a bank account.");
      return;
    }
    // Strip internal flags before sending.
    const rawItems = reverseJePreview.payload.line?.items ?? [];
    const items = rawItems.map(
      // Both internal flags have to go — `__extra` was previously left on the
      // line and sent to NetSuite along with it.
      // eslint-disable-next-line no-unused-vars
      ({ __bank, __extra, ...rest }) => rest,
    );

    // NetSuite rejects a single-subsidiary advanced intercompany JE outright, which
    // happens whenever the chosen bank account belongs to SFC (every line then sits
    // in SFC). Post a plain journalEntry instead: that record type has no per-line
    // subsidiary, so `lineSubsidiary` moves up to the header as `subsidiary` and
    // comes off the lines. Same helpers the memo rule uses, so the record type
    // decided here always matches the memo the preview showed.
    const lineSubIds = distinctLineSubsidiaries(items);
    const isSingleSubsidiary = isSingleSubsidiaryEntry(items);

    let cleanPayload;
    let mutation;
    if (isSingleSubsidiary) {
      // eslint-disable-next-line no-unused-vars
      const plainItems = items.map(({ lineSubsidiary, ...rest }) => rest);
      cleanPayload = {
        ...reverseJePreview.payload,
        // Fall back to the header subsidiary the preview was built with when no
        // line carried one at all.
        subsidiary: { id: lineSubIds[0] ?? String(reverseJePreview.payload.subsidiary?.id ?? "") },
        line: { ...reverseJePreview.payload.line, items: plainItems },
      };
      mutation = createPlainJeMut;
    } else {
      cleanPayload = {
        ...reverseJePreview.payload,
        line: { ...reverseJePreview.payload.line, items },
      };
      mutation = createJeMut;
    }

    try {
      await mutation.mutateAsync(cleanPayload);
      showSuccess(
        isSingleSubsidiary
          ? "Journal entry created (single subsidiary — posted as a normal journal entry)."
          : "Reverse journal entry created.",
      );
      setReverseJePreview(null);
    } catch (err) {
      showError(getApiErrorMessage(err) || "Failed to create reverse journal entry.");
    }
  };

  const handleCreate = async ({ clinic, receiver, amount, occurred_on, note, source }) => {
    try {
      await createMutation.mutateAsync({
        clinic_code_input: clinic, // backend resolves code → ID; no admin perms needed
        giver: userId,
        receiver,
        amount,
        occurred_on,
        note,
        source: source || "clinic",
      });
      showSuccess("Pending hand-off recorded.");
      return true;
    } catch (err) {
      showApiError(err, "Failed to record transaction.");
      return false;
    }
  };

  const handleDecide = async (row, decision) => {
    try {
      const status = decision === "confirm" ? "confirmed" : "rejected";
      await updateMutation.mutateAsync({ id: row.id, status });
      showSuccess(decision === "confirm" ? "Transaction confirmed." : "Transaction rejected.");
    } catch (err) {
      showApiError(err, "Failed to update transaction.");
    }
  };

  const handleDeposit = (clinicCode, onHandAvailable) => {
    const code = String(clinicCode).toUpperCase();
    const clinic = clinicByCode[code];
    if (!clinic) {
      showError(`Clinic info not loaded for ${code}.`);
      return;
    }
    const cashInHandId = clinic.netsuite_cash_in_hand;
    const subId = clinic.netsuite_subsidiary_id;
    if (!cashInHandId) {
      showError(`Clinic ${code} has no NetSuite Cash in Hand account configured.`);
      return;
    }
    if (!subId) {
      showError(`Clinic ${code} has no NetSuite subsidiary configured.`);
      return;
    }
    const amount = Number(onHandAvailable) || 0;
    if (amount <= 0) {
      showError("Nothing to deposit.");
      return;
    }
    const trandate = todayIso();
    const memo = `Cash Tranfer - Smiles Analytics - ${trandate}`;
    const sfcId = String(NETSUITE_ACCOUNT_IDS.SMILES_FIRST_CORPORATION);
    const loanId = String(NETSUITE_ACCOUNT_IDS.LOAN_FROM_SMILES_FIRST_CORPORATION);
    const cashSfcId = String(NETSUITE_ACCOUNT_IDS.CASH_SFC);
    const clinicSubId = String(subId);

    const payload = {
      subsidiary: { id: sfcId },
      memo,
      trandate,
      line: {
        items: [
          {
            lineSubsidiary: { id: sfcId },
            account: { id: cashSfcId },
            debit: amount,
            memo,
          },
          {
            lineSubsidiary: { id: sfcId },
            account: { id: loanId },
            credit: amount,
            memo,
          },
          {
            lineSubsidiary: { id: clinicSubId },
            account: { id: loanId },
            debit: amount,
            memo,
          },
          {
            lineSubsidiary: { id: clinicSubId },
            account: { id: String(cashInHandId) },
            credit: amount,
            memo,
          },
        ],
      },
    };
    setDepositPreview({ clinicCode: code, amount, payload });
  };

  const confirmDirectDeposit = async () => {
    if (!directDeposit) return;
    try {
      await depositMutation.mutateAsync({
        clinic_code: directDeposit.clinicCode,
        occurred_on: todayIso(),
        post_journal_entry: true,
      });
      showSuccess("Journal entry created and cash deposited to safe.");
      setDirectDeposit(null);
    } catch (err) {
      showError(getApiErrorMessage(err) || "Failed to deposit to safe.");
    }
  };

  const confirmDeposit = async () => {
    if (!depositPreview) return;
    try {
      await createJeMut.mutateAsync(depositPreview.payload);
    } catch (err) {
      showError(getApiErrorMessage(err) || "Failed to create journal entry. Deposit not applied.");
      return;
    }
    try {
      await depositMutation.mutateAsync({
        clinic_code: depositPreview.clinicCode,
        occurred_on: depositPreview.payload.trandate || todayIso(),
      });
      showSuccess("Journal entry created and cash deposited to safe.");
      setDepositPreview(null);
    } catch (err) {
      showError(
        getApiErrorMessage(err) ||
          "Journal entry created, but failed to deposit to safe. Please retry the deposit.",
      );
    }
  };

  // ---- Body (shared by both modes) -------------------------------------------
  // Only the page-level queries gate the body. The pending-inbox and history
  // listings are fetched per clinic INSIDE ClinicCashCard (so each can be paged
  // on its own), and each renders its own loading state — blocking the whole
  // page on them would hide the balances and the hand-off form for no reason.
  const isLoading = balanceQuery.isLoading || usersQuery.isLoading;
  const error = balanceQuery.error || usersQuery.error;

  let body;
  if (!userId) {
    body = (
      <Card title="Cash Management">
        <p className="text-palette-gray-secondary">Please log in to view cash management.</p>
      </Card>
    );
  } else if (!isControlled && clinicOptions.length === 0) {
    body = (
      <Card title="Cash Management">
        <p className="text-palette-gray-secondary">
          {canReadAll && clinicsLoading
            ? "Loading clinics…"
            : "You don't have access to any clinics yet."}
        </p>
      </Card>
    );
  } else if (!hasClinics) {
    body = (
      <Card title="Cash Management">
        <p className="text-palette-gray-secondary">
          Select at least one clinic above to view its cash management.
        </p>
      </Card>
    );
  } else {
    body = (
      <DataStateHandler isLoading={isLoading} error={error}>
        {!controlledDate && (
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs font-medium text-palette-gray-heading">Filter by day:</label>
            <input
              type="date"
              value={selectedDay}
              min={monthRange.from || undefined}
              max={monthRange.to || undefined}
              onChange={(e) => setSelectedDay(e.target.value)}
              className="rounded-md border border-palette-gray-border px-2 py-1 text-xs"
            />
            {selectedDay && (
              <button
                type="button"
                onClick={() => setSelectedDay("")}
                className="inline-flex items-center rounded-md border border-palette-gray-divider bg-palette-gray-soft px-2 py-1 text-xs font-medium text-palette-gray-heading hover:bg-palette-gray-subtle"
              >
                Clear
              </button>
            )}
            <span className="text-xs text-palette-gray-secondary">
              {selectedDay
                ? `Showing ${selectedDay}`
                : monthRange.from
                  ? `${monthRange.from} through ${monthRange.to}`
                  : ""}
            </span>
          </div>
        )}

        {uniqueClinics.map((code) => (
          <ClinicCashCard
            key={code}
            clinic={code}
            date={date}
            userId={userId}
            isAdminOrOrg={canSeeAdminData}
            balance={balanceByCode[code]}
            balanceLoading={balanceQuery.fetchStatus === "fetching"}
            depositJes={depositJesByCode[code] || []}
            depositJesLoading={
              depositJeSearchQuery.fetchStatus === "fetching" ||
              depositTxQuery.fetchStatus === "fetching"
            }
            receiverOptions={receiverOptionsByCode[code] || []}
            onCreate={handleCreate}
            onDecide={handleDecide}
            onDeposit={
              isAdminOrOrg
                ? handleDeposit
                : canWriteClinicData(user)
                  ? (clinicCode, amount) =>
                      setDirectDeposit({ clinicCode: String(clinicCode).toUpperCase(), amount })
                  : undefined
            }
            busy={busy}
            createPending={createMutation.isPending}
          />
        ))}
      </DataStateHandler>
    );
  }

  const reverseJeTable = canSeeAdminData ? (
    <Card className="mb-6">
      <h3 className="text-lg font-semibold text-palette-gray-primary mb-1">
        Reverse Journal Entries
      </h3>
      <p className="text-xs text-palette-gray-placeholder mb-3">
        Memo contains &ldquo;{REVERSE_JE_MEMO}&rdquo; (intercompany) or &ldquo;
        {CASH_TO_BANK_JE_MEMO}&rdquo; (single subsidiary)
      </p>
      {reverseJeListQuery.isLoading || reversePlainJeListQuery.isLoading ? (
        <Loading size="sm" text="Loading reverse journal entries..." />
      ) : reverseJeList.length === 0 ? (
        <p className="text-sm text-palette-gray-secondary">
          No reverse journal entries found in NetSuite.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-palette-gray-soft text-left text-xs uppercase text-palette-gray-secondary">
              <tr>
                <th className="px-3 py-2">Transaction Date</th>
                <th className="px-3 py-2">Transaction ID</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Subsidiary</th>
                <th className="px-3 py-2">Memo</th>
                <th className="px-3 py-2 text-right">Link</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-palette-gray-subtle">
              {reverseJePageRows.map((je, idx) => {
                const isPlain = je.record_type === "journalEntry";
                return (
                  // Keyed on record type too: the two NetSuite tables number their
                  // records independently, so ids can collide across them.
                  <tr key={`${je.record_type ?? "je"}-${je.id ?? idx}`}>
                    <td className="px-3 py-2 text-palette-gray-badge">{je.trandate || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {je.tranid || je.transactionnumber || "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex whitespace-nowrap rounded-full px-2 py-[1px] text-[10px] font-semibold ring-1 ring-inset ${
                          isPlain
                            ? "bg-palette-slate-muted text-palette-slate-heading ring-palette-slate-ring"
                            : "bg-palette-indigo-soft text-palette-indigo-text ring-palette-indigo-border"
                        }`}
                        title={
                          isPlain
                            ? "Normal journal entry — every line sat in one subsidiary"
                            : "Advanced intercompany journal entry — spans two or more subsidiaries"
                        }
                      >
                        {isPlain ? "Journal" : "Intercompany"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-palette-gray-badge">{je.subsidiary || "—"}</td>
                    <td className="px-3 py-2 text-palette-gray-badge">{je.memo || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      {je.link ? (
                        <a
                          href={je.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md bg-palette-emerald-soft text-palette-emerald-hover border border-palette-emerald-border hover:bg-palette-emerald-muted"
                          title="Open transaction in NetSuite"
                        >
                          View Transaction
                        </a>
                      ) : (
                        <span className="text-palette-gray-placeholder">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="mt-3 rounded-md border border-palette-slate-divider bg-white">
            <Pagination
              page={reverseJePage}
              pageSize={reverseJePageSize}
              total={reverseJeTotal}
              onPageChange={setReverseJePage}
              onPageSizeChange={(size) => {
                setReverseJePage(1);
                setReverseJePageSize(size);
              }}
              pageSizeOptions={[5, 10, 20, 50, 100]}
            />
          </div>
        </div>
      )}
    </Card>
  ) : null;

  // ---- Shared account header (Card + Totals popover) -------------------------
  // showPendingTotal: the controlled (Admin/Management tab) branch has never
  // shown the pending total in the footer, only the standalone route has.
  // Preserving that split via a flag instead of re-diverging the JSX pending
  // a decision on whether the controlled tabs should show it too.
  function renderAccountHeader({ showPendingTotal }) {
    if (!account) return null;
    return (
      <div className="mb-6 flex items-start gap-3">
        <div className="w-80">
          <Card>
            <div>
              <h3 className="text-lg font-semibold text-palette-gray-primary">
                Account Information
              </h3>
              <p className="text-sm text-palette-gray-badge mb-3">
                {account.displaynamewithhierarchy}
              </p>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wide text-palette-gray-secondary">
                  Balance
                </div>
                <div className="text-2xl font-semibold text-palette-gray-primary">
                  {formatMoney(account.balance)}
                </div>
              </div>
            </div>
          </Card>
        </div>
        <div className="flex flex-col gap-2">
          {isOrganizerOrAdmin(user) && (
            <Button variant="primary" disabled={busy} onClick={handleOpenReverseJe}>
              Create Reverse Journal Entry
            </Button>
          )}
          <Tooltip
            variant="light"
            align="start"
            placement="bottom"
            interactive
            width={384}
            triggerAs="div"
            contentClassName="!rounded-xl !p-3 !border-palette-gray-divider text-palette-gray-primary max-h-[60vh] overflow-y-auto"
            content={
              <>
                <div className="text-xs font-semibold uppercase text-palette-gray-secondary mb-2">
                  Clinic Balances
                </div>
                {uniqueClinics.length === 0 ? (
                  <p className="text-xs text-palette-gray-secondary">No clinics selected.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-palette-gray-placeholder">
                        <th className="pb-1 text-left font-normal">Clinic</th>
                        <th className="pb-1 text-right font-normal">Balance</th>
                        <th className="pb-1 text-right font-normal text-palette-amber-label">
                          Pending
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-palette-gray-subtle">
                      {uniqueClinics.map((code) => {
                        const b = balanceByCode[code];
                        const loading = balanceQuery.fetchStatus === "fetching";
                        const beforeCheckpoint = !!(
                          b?.checkpoint_date &&
                          date &&
                          date < b.checkpoint_date
                        );
                        return (
                          <tr key={code}>
                            <td className="py-1 font-medium text-palette-gray-heading">{code}</td>
                            <td className="py-1 text-right font-mono">
                              {loading ? (
                                <span className="text-palette-gray-placeholder text-xs">…</span>
                              ) : beforeCheckpoint ? (
                                <span
                                  className="text-palette-gray-placeholder text-xs"
                                  title={`No balance recorded for ${date}. Earliest: ${b.checkpoint_date}.`}
                                >
                                  N/A
                                </span>
                              ) : b ? (
                                formatMoney(b.balance)
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="py-1 text-right font-mono text-palette-amber-badge">
                              {loading ? (
                                <span className="text-palette-gray-placeholder text-xs">…</span>
                              ) : b && Number(b.pending_out) > 0 ? (
                                formatMoney(b.pending_out)
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-palette-gray-border">
                        <td className="pt-1.5 font-semibold text-palette-gray-text">Total</td>
                        <td className="pt-1.5 text-right font-mono font-semibold text-palette-gray-text">
                          {formatMoney(
                            uniqueClinics.reduce((s, code) => {
                              const b = balanceByCode[code];
                              // Skip clinics whose balance is not known for the
                              // selected date (as_of before the checkpoint).
                              if (b?.checkpoint_date && date && date < b.checkpoint_date) {
                                return s;
                              }
                              return s + Number(b?.balance || 0);
                            }, 0),
                          )}
                        </td>
                        {showPendingTotal ? (
                          <td className="pt-1.5 text-right font-mono font-semibold text-palette-amber-badge">
                            {formatMoney(
                              uniqueClinics.reduce(
                                (s, code) => s + Number(balanceByCode[code]?.pending_out || 0),
                                0,
                              ),
                            )}
                          </td>
                        ) : (
                          <td className="pt-1.5" />
                        )}
                      </tr>
                    </tfoot>
                  </table>
                )}
              </>
            }
          >
            <Button variant="secondary" className="w-full">
              View Totals
            </Button>
          </Tooltip>
        </div>
      </div>
    );
  }

  // ---- Shared preview modals ---------------------------------------------
  const previewModals = (
    <>
      {directDeposit && (
        <ConfirmModal
          isOpen={!!directDeposit}
          onClose={() => !depositMutation.isPending && setDirectDeposit(null)}
          onConfirm={confirmDirectDeposit}
          title={`Deposit to Safe — ${directDeposit.clinicCode}`}
          confirmText={depositMutation.isPending ? "Sending..." : "Confirm & Send"}
          confirmVariant="primary"
          cancelText="Cancel"
          isLoading={depositMutation.isPending}
          message={
            <p className="text-sm text-palette-gray-text">
              Deposit your available cash of{" "}
              <span className="font-semibold">{formatMoney(directDeposit.amount)}</span> at{" "}
              {directDeposit.clinicCode} to the safe. This posts the cash transfer journal entry to
              NetSuite (clinic Cash in Hand → SFC) dated {todayIso()}.
            </p>
          }
        />
      )}
      {depositPreview && (
        <ConfirmModal
          isOpen={!!depositPreview}
          onClose={() =>
            !createJeMut.isPending && !depositMutation.isPending && setDepositPreview(null)
          }
          onConfirm={confirmDeposit}
          title={`Deposit to Safe — ${depositPreview.clinicCode}`}
          size="xl"
          confirmText={
            createJeMut.isPending || depositMutation.isPending ? "Sending..." : "Confirm & Send"
          }
          confirmVariant="primary"
          cancelText="Cancel"
          isLoading={createJeMut.isPending || depositMutation.isPending}
          message={
            <DepositToSafePreview
              preview={{ clinicCode: depositPreview.clinicCode, payload: depositPreview.payload }}
              onChange={(patch) =>
                setDepositPreview((prev) =>
                  prev ? { ...prev, payload: { ...prev.payload, ...patch } } : prev,
                )
              }
            />
          }
        />
      )}
      {reverseJePreview && (
        <ConfirmModal
          isOpen={!!reverseJePreview}
          // Either mutation can be in flight — which one depends on whether the
          // entry turned out to be single-subsidiary.
          onClose={() => !reverseJeSending && setReverseJePreview(null)}
          onConfirm={confirmReverseJe}
          title="Create Reverse Journal Entry"
          size="3xl"
          confirmText={reverseJeSending ? "Sending..." : "Confirm & Send"}
          confirmVariant="primary"
          cancelText="Cancel"
          isLoading={reverseJeSending}
          confirmDisabled={
            !(Number(reverseJePreview.amount) > 0) || !reverseJePreview.bankAccountId
          }
          message={
            <ReverseJournalEntryPreview
              preview={reverseJePreview}
              onChange={handleReverseJeChange}
            />
          }
        />
      )}
    </>
  );

  // ---- Controlled: just the body (parent owns Page + selectors) -------------
  if (isControlled) {
    return (
      <>
        {renderAccountHeader({ showPendingTotal: false })}
        {reverseJeTable}
        {body}
        {previewModals}
      </>
    );
  }

  // ---- Standalone: full page with built-in MonthlyClinicSelection -----------
  return (
    <Page>
      {renderAccountHeader({ showPendingTotal: true })}
      {reverseJeTable}
      <div className="mb-4">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <MonthlyClinicSelection
            months={ownMonths}
            onMonthsChange={setOwnMonths}
            disableCurrentAndFuture={false}
            clinicOptions={clinicOptions}
            clinicValue={ownSelectedClinics}
            onClinicChange={setOwnSelectedClinics}
          />
        </div>
      </div>
      {body}
      {previewModals}
    </Page>
  );
}
